import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, X, CheckCircle, ChatCircleDots, CalendarBlank, CalendarX, Trophy, Megaphone } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'zojuist';
    if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
    return `${Math.floor(diff / 86400)} dag${Math.floor(diff / 86400) > 1 ? 'en' : ''} geleden`;
}

// Pick an icon + accent colour based on the notification category/tag.
function categoryIcon(item) {
    const c = (item.category || '').toLowerCase();
    const tag = (item.tag || '').toLowerCase();
    if (c === 'chat' || tag === 'chat-message') return { Icon: ChatCircleDots, color: '#3B82F6' };
    if (c === 'scheduled' || tag === 'scheduled-lesson') return { Icon: CalendarBlank, color: '#10B981' };
    if (c === 'reminder' || tag === 'lesson-reminder') return { Icon: CalendarBlank, color: '#F59E0B' };
    if (c === 'cancellation' || tag === 'lesson-cancelled') return { Icon: CalendarX, color: '#EF4444' };
    if (tag === 'lesson-confirmed') return { Icon: CheckCircle, color: '#10B981' };
    if (c === 'achievement') return { Icon: Trophy, color: '#D4A537' };
    return { Icon: Megaphone, color: '#D4A537' };
}

/**
 * Universal in-app notification bell for both instructors and students.
 * Pulls from /api/notifications, shows unread badge, and lets the user
 * dismiss or open each item.
 */
export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [wiggling, setWiggling] = useState(false);
    const prevUnreadRef = useRef(0);
    const wrapperRef = useRef(null);

    const fetchItems = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
            setItems(data || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        fetchItems();
        const t = setInterval(fetchItems, 20000);
        return () => clearInterval(t);
    }, [fetchItems]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const unread = items.filter(i => !i.read).length;

    // Wiggle when a new notification arrives
    useEffect(() => {
        if (unread > prevUnreadRef.current && prevUnreadRef.current > 0) {
            setWiggling(true);
            setTimeout(() => setWiggling(false), 800);
        }
        prevUnreadRef.current = unread;
    }, [unread]);

    const markRead = async (id) => {
        try {
            await axios.post(`${API}/notifications/${id}/read`, null, { withCredentials: true });
            setItems(prev => prev.map(i => i.id === id ? { ...i, read: true } : i));
        } catch { /* ignore */ }
    };

    const markAllRead = async () => {
        try {
            await axios.post(`${API}/notifications/read-all`, null, { withCredentials: true });
            setItems(prev => prev.map(i => ({ ...i, read: true })));
        } catch { /* ignore */ }
    };

    const removeItem = async (id) => {
        try {
            await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
            setItems(prev => prev.filter(i => i.id !== id));
        } catch { /* ignore */ }
    };

    const handleItemClick = (item) => {
        markRead(item.id);
        if (item.url && item.url !== '/dashboard') {
            // Trigger known event-based deep-links instead of full navigation when possible
            if (item.url.includes('?open=chat')) {
                window.dispatchEvent(new CustomEvent('qr:open-chat'));
            } else if (item.url.includes('?action=cancel')) {
                window.dispatchEvent(new CustomEvent('qr:open-cancel'));
            } else if (item.tag === 'driven-check' || (item.url && item.url.includes('open=driven-check'))) {
                const idMatch = item.url && item.url.match(/[?&]id=([^&]+)/);
                const id = idMatch ? idMatch[1] : null;
                if (id) window.dispatchEvent(new CustomEvent('qr:open-driven-check', { detail: { id } }));
            }
        }
        setOpen(false);
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                onClick={() => setOpen(o => !o)}
                className={`relative text-[#D4A537] hover:text-[#E5B84A] transition-colors p-2 rounded-lg hover:bg-[#3D4A5C] ${wiggling ? 'animate-wiggle' : ''}`}
                title="Meldingen"
                data-testid="notification-bell-button"
                aria-label={`Meldingen${unread > 0 ? ` (${unread} ongelezen)` : ''}`}
            >
                <Bell size={20} weight="fill" />
                {unread > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse"
                        data-testid="notification-bell-unread"
                    >
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[60px] sm:top-full sm:mt-2 sm:w-96 bg-[#1A202C] border border-[#4A5568] rounded-xl shadow-2xl z-[60] overflow-hidden"
                    data-testid="notification-bell-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#4A5568]">
                        <div>
                            <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Meldingen</div>
                            <div className="text-[11px] text-gray-500">{unread > 0 ? `${unread} ongelezen` : 'Alles bekeken'}</div>
                        </div>
                        <div className="flex items-center gap-1">
                            {unread > 0 && (
                                <button
                                    onClick={markAllRead}
                                    className="text-[11px] text-[#D4A537] hover:text-[#E5B84A] px-2 py-1 rounded hover:bg-[#3D4A5C]/40"
                                    data-testid="notification-mark-all"
                                >
                                    Alles gelezen
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                className="text-gray-500 hover:text-white p-1"
                                aria-label="Sluiten"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="py-10 text-center px-4">
                                <div className="w-12 h-12 rounded-full bg-[#2D3748] mx-auto mb-2 flex items-center justify-center">
                                    <Bell size={20} className="text-gray-500" />
                                </div>
                                <p className="text-sm text-gray-400">Nog geen meldingen</p>
                            </div>
                        ) : (
                            items.map((item) => {
                                const { Icon, color } = categoryIcon(item);
                                return (
                                    <div
                                        key={item.id}
                                        className={`px-3 py-3 border-b border-[#4A5568]/60 last:border-b-0 flex items-start gap-2 group cursor-pointer hover:bg-[#2D3748]/70 transition-colors ${item.read ? 'opacity-60' : ''}`}
                                        onClick={() => handleItemClick(item)}
                                        data-testid={`notification-item-${item.id}`}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${color}22`, border: `1px solid ${color}66` }}
                                        >
                                            <Icon size={16} weight="fill" style={{ color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {item.title}
                                                {!item.read && (
                                                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-500 align-middle" />
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400 mt-0.5 break-words">{item.body}</div>
                                            <div className="text-[10px] text-gray-500 mt-1">{timeAgo(item.created_at)}</div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                                            className="text-gray-600 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                            aria-label="Verwijder melding"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
